'use client'

import { useEffect, useState } from 'react'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import { FormProvider, useForm } from 'react-hook-form'

import { ContactFormDto } from '@/domains/contact/application/dto/createContact.dto'
import ContactSubmitButton from '@/ui/components/contact/action/send'
import Form from '@/ui/components/contact/form/form'
import Message from '@/ui/components/contact/form/message'
import { useSendContact } from '@/ui/hooks/contact/use-send-contact'
import { showError, showSuccess } from '@/utils/toast'

interface RecaptchaExecutorProps {
  onToken: (token: string) => void
  onError: () => void
}

function RecaptchaExecutor({ onToken, onError }: RecaptchaExecutorProps) {
  const { executeRecaptcha } = useGoogleReCaptcha()

  useEffect(() => {
    if (!executeRecaptcha) return
    executeRecaptcha('contact_form_submit')
      .then(onToken)
      .catch((error) => {
        console.error('Erreur client reCAPTCHA:', error)
        onError()
      })
  }, [executeRecaptcha, onToken, onError])

  return null
}

/**
 * Îlot client du formulaire. Le reste de la section contact est rendu
 * côté serveur : seul ce bloc a besoin d'interactivité, et le script
 * reCAPTCHA n'est chargé qu'au moment de l'envoi.
 */
export default function ContactForms() {
  const { mutateAsync } = useSendContact()
  const [submitData, setSubmitData] = useState<{
    onToken: (token: string) => void
    onError: () => void
  } | null>(null)

  const methods = useForm<ContactFormDto>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const onSubmit = async (data: ContactFormDto) =>
    new Promise<void>((resolve, reject) => {
      const onToken = async (token: string) => {
        try {
          await mutateAsync({ ...data, recaptchaToken: token })
          methods.reset()
          showSuccess('Formulaire envoyé avec succès.')
          setSubmitData(null)
          resolve()
        } catch (error) {
          showError(
            error instanceof Error
              ? error.message
              : "Erreur lors de l'envoi du formulaire"
          )
          setSubmitData(null)
          reject(error)
        }
      }

      const onError = () => {
        showError('Erreur reCAPTCHA. Veuillez vérifier votre connexion.')
        setSubmitData(null)
        reject(new Error('Erreur reCAPTCHA.'))
      }

      setSubmitData({ onToken, onError })
    })

  return (
    <>
      {submitData && (
        <GoogleReCaptchaProvider
          reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''}
          scriptProps={{ async: true, defer: true, appendTo: 'head' }}
        >
          <RecaptchaExecutor
            onToken={submitData.onToken}
            onError={submitData.onError}
          />
        </GoogleReCaptchaProvider>
      )}

      <FormProvider {...methods}>
        {/* Le rejet est déjà signalé à l'utilisateur par un toast ; on le
            rattrape ici pour éviter un « Uncaught (in promise) ». */}
        <form
          onSubmit={(event) => {
            void methods.handleSubmit(onSubmit)(event).catch(() => {})
          }}
          noValidate
          autoComplete="on"
        >
          <div className="flex flex-col gap-4">
            <Form />
            <Message />
            <div className="pt-4">
              <ContactSubmitButton />
            </div>
          </div>

          <p className="mt-8 text-[0.6875rem] leading-relaxed text-mist">
            Ce site est protégé par reCAPTCHA. La{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="link-draw text-chalk"
            >
              Politique de confidentialité
            </a>{' '}
            et les{' '}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noreferrer"
              className="link-draw text-chalk"
            >
              Conditions d&apos;utilisation
            </a>{' '}
            de Google s&apos;appliquent.
          </p>
        </form>
      </FormProvider>
    </>
  )
}
